import { Injectable } from '@angular/core';
import findIndex from 'lodash-es/findIndex';
import map from 'lodash-es/map';
import uniqBy from 'lodash-es/uniqBy';

import { V12RetailFinance } from './v12-retail-finance.namespace';

import { CheckoutFormComponentDef } from '@df/checkout/checkout-form.component.def';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { CacheService } from '@df/core/cache/cache';
import { Events } from '@df/core/service/event';
import { TimeoutService } from '@df/ng/timeout.service';
import { Basket } from '@df/session/api/basket/basket';

@Injectable({ providedIn: 'root' })
export class V12RetailFinanceService {
    private checkoutFormComponent?: CheckoutFormComponentDef;

    private _isAvailable = false;

    private onDataUpdateDebounced: () => void;

    constructor(
        private events: Events,
        private basket: Basket,
        private cacheService: CacheService,
        private timeoutService: TimeoutService
    ) {
        this.onDataUpdateDebounced = this.timeoutService.debounce(() => this.onDataUpdate(), 200);
        this.events.on(Checkout.EVENTS.Create).subscribe((checkoutFormComponent: CheckoutFormComponentDef) => {
            this.checkoutFormComponent = checkoutFormComponent;
            this._isAvailable = this.getAvailability();
        });

        this.events.on(Checkout.EVENTS.Destroy).subscribe(() => {
            this.checkoutFormComponent = undefined;
            this._isAvailable = false;
        });

        this.events.on(Checkout.EVENTS.PaymentMethodsUpdate).subscribe(() => this.onDataUpdateDebounced());
        this.events.on(Checkout.EVENTS.TotalsUpdate).subscribe(() => this.onDataUpdateDebounced());
        this.basket.subscribe(() => this.onDataUpdateDebounced());

        this.events.on(Checkout.EVENTS.SetPaymentMethodsBefore).subscribe((methods: Checkout.IPaymentMethod[]) => {
            if (!this.isAvailable && methods?.length) {
                const index = findIndex(methods, method => {
                    const code = method.code;
                    if (typeof code === 'string') {
                        return code === V12RetailFinance.PaymentMethod.VFinance;
                    }
                    return false;
                });
                if (index > -1) {
                    methods.splice(index, 1);
                }
            }
        });
    }

    private onDataUpdate(): void {
        if (this.method) {
            this.isAvailable = this.getAvailability();
        }
    }

    ////////////////////////////////////////////
    ////////
    ////////    Availability
    ////////
    ////////////////////////////////////////////

    set isAvailable(value: boolean) {
        const was = this.isAvailable;
        this._isAvailable = value;
        if (was !== value) {
            this.events.broadcast(Checkout.EVENTS.RefreshPaymentMethods);
        }
    }

    get isAvailable(): boolean {
        return this._isAvailable;
    }

    private getAvailability(): boolean {
        const total = this.total;
        if (this.method && total) {
            const packs = this.packOptions;
            const deposits = this.depositOptions;
            return !!deposits.find(deposit => !!this.getAvailablePacksForDeposit(deposit, packs, total)?.length);
        }
        return false;
    }

    ////////////////////////////////////////////
    ////////
    ////////    Deposit
    ////////
    ////////////////////////////////////////////

    getAvailableDepositOptions(): V12RetailFinance.IDepositOption[] {
        const options = this.depositOptions;
        if (options?.length) {
            return options.filter(i => !!this.getAvailablePacksForDeposit(i));
        }
        return [];
    }

    get depositOptions(): V12RetailFinance.IDepositOption[] {
        return this.cacheService.get(['v12RetailFinanceDepositOptions', this.method], () => {
            if (this.method?.config?.v12percent) {
                return uniqBy(
                    map(this.method.config.v12percent, (value, key) => {
                        return {
                            value: parseFloat(key),
                            title: value
                        };
                    }),
                    i => i.value
                );
            }
            return [];
        });
    }

    ////////////////////////////////////////////
    ////////
    ////////    Package plan
    ////////
    ////////////////////////////////////////////

    getAvailablePacksForDeposit(
        deposit: V12RetailFinance.IDepositOption,
        packOptions = this.packOptions,
        total = this.total
    ): V12RetailFinance.IPackOption[] {
        return packOptions.filter(packOption => {
            const { min, max } = this.getMinMax(deposit, packOption);
            return min <= total && max >= total;
        });
    }

    get packOptions(): V12RetailFinance.IPackOption[] {
        return this.cacheService.get(['v12RetailFinancePackOptions', this.method], () => {
            if (this.method?.config?.v12pack) {
                return uniqBy(
                    this.method.config.v12pack.map(i => {
                        const v12package = i.package;
                        const packageParts = v12package.split('/');
                        return {
                            id: v12package,
                            months: parseInt(packageParts[2], 10),
                            interest: parseFloat(packageParts[3]),
                            apr: parseFloat(packageParts[4]),
                            title: packageParts[5],
                            minFinanceAmount: i.min_finance_amount || 0,
                            maxFinanceAmount: i.max_finance_amount || Infinity
                        };
                    }),
                    i => i.id
                );
            }
            return [];
        });
    }

    ////////////////////////////////////////////
    ////////
    ////////    Helpers
    ////////
    ////////////////////////////////////////////

    get total(): number | undefined {
        return this.basket?.totals?.grand_total;
    }

    getMontlyPayment(amount: number, apr: number, terms: number): number {
        const payment = (((amount * apr) / 12) * Math.pow(1 + apr / 12, terms)) / (Math.pow(1 + apr / 12, terms) - 1);
        return Math.round(payment * 100) / 100;
    }

    private get method(): V12RetailFinance.IPaymentMethod | undefined {
        return this.checkoutFormComponent?.paymentMethods.find(i => i.code === V12RetailFinance.PaymentMethod.VFinance) as
            | V12RetailFinance.IPaymentMethod
            | undefined;
    }

    private getMinMax(deposit: V12RetailFinance.IDepositOption, packOption: V12RetailFinance.IPackOption): { min: number; max: number } {
        const loan = (100 - deposit.value) * 0.01;

        return {
            min: fixDecimals(packOption.minFinanceAmount / loan, true),
            max: fixDecimals(packOption.maxFinanceAmount / loan)
        };

        function fixDecimals(value: number, up = false): number {
            const times1000 = value * 1000;
            if (times1000 % 10 !== 0) {
                return parseFloat(((times1000 + (up ? 10 : 0)) / 1000).toFixed(2));
            }
            return value;
        }
    }
}
